import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DoorOpen, KeyRound, Loader2 } from "lucide-react";
import api from "../lib/api";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

function DoorRow({ d }) {
  const [busy, setBusy] = useState(false);
  const unlock = async () => {
    if (!window.confirm(`Unlock "${d.name}" at ${d.location || d.place_name}?`)) return;
    setBusy(true);
    try { await api.post(`/kisi/locks/${d.lock_id}/unlock`, null, { params: { place_id: d.place_id } }); toast.success(`${d.name} unlocked`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Unlock failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5">
      <div className="min-w-0">
        <p className="text-sm text-brand-ink truncate">{d.name}</p>
        <p className="text-[11px] text-brand-tertiary">{d.location || d.place_name}</p>
      </div>
      <button onClick={unlock} disabled={busy} data-testid={`member-door-unlock-${d.lock_id}`}
        className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-brand-border px-3 py-1 text-xs text-brand-ink hover:bg-brand-ink hover:text-brand-page disabled:opacity-50 transition-colors">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <DoorOpen className="h-3 w-3" />} Unlock
      </button>
    </div>
  );
}

export default function MemberAccess({ companyId }) {
  const [a, setA] = useState(null);
  useEffect(() => { api.get(`/members/${companyId}/access`).then(({ data }) => setA(data)).catch(() => setA(false)); }, [companyId]);

  if (a === null) return <p className="text-sm text-brand-muted flex items-center gap-2" data-testid="member-access-loading"><Loader2 className="h-4 w-4 animate-spin" /> Checking Kisi… (big companies take up to 30s)</p>;
  if (a === false) return <p className="text-sm text-brand-muted">Couldn't load door access.</p>;
  if (!a.configured) return <p className="text-sm text-brand-muted">Kisi isn't connected.</p>;
  const on = a.contacts.filter((c) => c.access_enabled).length;

  return (
    <div className="space-y-4" data-testid="member-access">
      <p className="text-sm text-brand-ink flex items-center gap-2" data-testid="member-access-summary">
        <KeyRound className="h-4 w-4 text-brand-gold" /> {on} of {a.contacts.length} contacts have Kisi access
        {a.partial && <span className="text-[11px] text-amber-700">(Kisi was busy — some contacts may be missing, reopen to retry)</span>}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {a.contacts.map((c) => (
          <span key={c.email || c.name} title={c.last_entry ? `Last entry ${when(c.last_entry)}` : "No recent entry"}
            className={`text-[11px] rounded-full px-2 py-0.5 border ${c.access_enabled ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-brand-border text-brand-tertiary"}`}>
            {c.name}{c.found ? "" : " · no Kisi"}
          </span>
        ))}
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.14em] text-brand-tertiary mb-2">Doors they can open ({a.doors.length})</p>
        <div className="rounded-lg border border-brand-border bg-brand-surface divide-y divide-brand-border" data-testid="member-doors">
          {a.doors.map((d) => <DoorRow key={d.lock_id} d={d} />)}
          {!a.doors.length && <p className="px-4 py-3 text-sm text-brand-tertiary">No door groups assigned.</p>}
        </div>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.14em] text-brand-tertiary mb-2">Recent entries (30 days)</p>
        <div className="rounded-lg border border-brand-border bg-brand-surface divide-y divide-brand-border" data-testid="member-entries">
          {a.entries.map((e, i) => (
            <div key={i} className="px-4 py-2 flex items-center justify-between gap-3 text-xs">
              <span className="text-brand-ink truncate">{e.person} <span className="text-brand-tertiary">· {e.door}{e.location ? `, ${e.location}` : ""}</span></span>
              <span className="text-brand-muted shrink-0">{when(e.at)}</span>
            </div>
          ))}
          {!a.entries.length && <p className="px-4 py-3 text-sm text-brand-tertiary">No entries in the last 30 days.</p>}
        </div>
      </div>
    </div>
  );
}
